import { Sparkles, Zap, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * ModuleExamplesBar — prominent CTA strip rendered directly under the
 * module page title. Each species button autofills the form and triggers
 * the analysis run via the parent's onSelect callback.
 *
 * UX intent: brand-new users land on a page, see "Try with one click —
 * Seabream / Seabass / Tilapia", click the species closest to their farm,
 * and within ~2s see a real result. Maximum aha-moment, zero data entry.
 *
 * Prompt 3 change: previously hidden from unapproved (read-only) users.
 * Now always visible — but clicking a species while read-only shows a
 * Sonner toast prompting the user to wait for admin approval. This makes
 * the platform's value visible to prospects before they've been approved.
 */
export default function ModuleExamplesBar({ examples = [], onSelect, moduleLabel, isReadOnly = false }) {
  if (!examples.length) return null;

  const handleClick = (ex) => {
    if (isReadOnly) {
      toast.info("Approval required to run examples — hang tight, an admin will approve your account shortly.", {
        icon: <Lock className="w-4 h-4" />,
        duration: 4500,
      });
      return;
    }
    onSelect(ex);
  };

  return (
    <div
      className="rounded-xl border-2 border-cyan-200 bg-gradient-to-r from-cyan-50 via-white to-cyan-50 p-4 sm:p-5 mb-6 shadow-sm"
      data-testid="module-examples-bar"
      data-readonly={isReadOnly ? 'true' : 'false'}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="rounded-full bg-cyan-600 p-2 shadow-sm">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-sm sm:text-base">
              {isReadOnly
                ? `See what a ${moduleLabel || 'module'} report looks like.`
                : `New here? Try this ${moduleLabel || 'module'} with one click.`}
            </p>
            <p className="text-xs sm:text-sm text-slate-600">
              {isReadOnly
                ? 'Preview only — you\'ll be able to run these once an admin approves your account.'
                : "Pick a species below — we'll fill the form with a realistic example and run it instantly."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {examples.map((ex) => (
            <div key={ex.species_label} className="flex flex-col items-start">
              <Button
                type="button"
                size="sm"
                onClick={() => handleClick(ex)}
                className={`${ex.badge_color} border font-semibold hover:opacity-90 transition-all hover:scale-[1.03] ${isReadOnly ? 'opacity-70' : ''}`}
                variant="outline"
                data-testid={`example-btn-${ex.species_label.toLowerCase()}`}
                aria-disabled={isReadOnly}
              >
                {isReadOnly ? <Lock className="h-3.5 w-3.5 mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
                Try {ex.species_label}
              </Button>
              {ex.source_record_name && (
                <span
                  className="text-[10px] text-slate-500 mt-1 ml-1 max-w-[180px] truncate"
                  title={`Loads saved record: ${ex.source_record_name}`}
                  data-testid={`example-file-name-${ex.species_label.toLowerCase()}`}
                >
                  {ex.source_record_name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
